/**
 * Represents a Route entry in the routing table
 */
export type Route<Origin extends string> = {
  /**
   * A pattern to match the pathname against. If set to `"*"` this route
   * is considered the default.
   *
   * If the pattern should match if it is a prefix of the pathname, add an
   * asteriks at the end, e.g. `/creative*`
   */
  pattern: string;
  /**
   * The origin it should route to.
   */
  origin: Origin;
  /**
   * The name of the launchdarkly flag that it should check to see if the route
   * is active. Always assumes it is diabled, unless LD responds with true.
   *
   * Flag should always be a boolean value and indicate if the route
   * should be enabled
   */
  flag?: string;

  /**
   * sensitive indicates if a route should be considered sensitive,
   * in which case it'll not be exposed on the public endpoint
   */
  sensitive?: boolean;
};

export type Routes<Origin extends string> = Array<Route<Origin>>;

export type OriginConfig = {
  /**
   * The hostname of the origin. Unless `useAltHostHeader` is enabled
   * this needs to be an address within the zone. If you wish to call to
   * an origin outside of the cloudflare zone this means setting up a
   * CNAME alias in the zone to the external origin. For more info see the docs
   * for `resolveOverride` on https://developers.cloudflare.com/workers/runtime-apis/request/
   */
  hostname: string;
  /**
   * useAltHostHeader enables an alternative host header which can be used
   * in scenario's where the host header cannot be different from the origin
   * address.
   *
   * This is often the case when the origin server is a CDN style network,
   * where lots of domains resolve to the same address and internal routing is
   * based on the host header. If we override the host header to be different
   * than the original address of the origin server the request won't reach the
   * origin. When enabling `useAltHostHeader` instead of overriding the host
   * header we send an `x-alt-host` that the application can read to determine
   * the public facing host name of the site.
   */
  useAltHostHeader?: boolean;

  /**
   * sensitive indicates if an origin should be considered sensitive,
   * in which case it'll not be exposed on the public endpoint
   */
  sensitive?: boolean;
};

export const handler = <Origin extends string>(
  originMapping: {[key: string]: OriginConfig},
  routes: Routes<Origin>
): ((req: Request) => Response) => {
  const nonSensitiveOrigins = Object.entries(originMapping)
    .filter(([_, originConfig]) => {
      return !originConfig.sensitive;
    })
    .map(([originName]) => originName);
  const nonSensitiveRoutes = routes.filter((route) => !route.sensitive);
  return (): Response => {
    return new Response(
      JSON.stringify({
        origins: nonSensitiveOrigins,
        routes: nonSensitiveRoutes,
      }),
      {
        headers: {'content-type': 'application/json'},
      }
    );
  };
};
